import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { formatDate, toNativeDate, toOptionalDateString } from 'utils/date';

import { LoadingButton } from '@mui/lab';
import { Alert, Button } from '@mui/material';

import { ControlledTextField, Dialog } from 'components/shared';
import ControlledValidFromValidTo from 'components/shared/ControlledFields/ControlledValidFromValidTo';

import type {
  CopyOpeningScheduleDialogProps,
  CopyOpeningScheduleFormValues
} from '../../OpeningHours.types';
import { useCopyOpeningSchedule } from '../../hooks/useCopyOpeningSchedule';
import {
  closesOriginal,
  getCopyDefaults
} from '../../utils/copyOpeningSchedule';
import { copySchema } from './CopyOpeningScheduleDialog.schema';
import {
  StyledCopyActions,
  StyledCopyFields
} from './CopyOpeningScheduleDialog.styles';

/**
 * Asks where a copy goes before making it: its name, its first and its last day. The copy is saved
 * straight away; a still valid original is then valid to the copy's first day, in the same request.
 */
const CopyOpeningScheduleDialog = ({
  planUnitId,
  schedule,
  onClose,
  onCopied
}: CopyOpeningScheduleDialogProps) => {
  'use memo';
  const { t } = useTranslation('common', {
    keyPrefix: 'planUnitHome.openingHours'
  });

  const { control, handleSubmit, reset } =
    useForm<CopyOpeningScheduleFormValues>({
      resolver: yupResolver(copySchema(schedule)),
      mode: 'onSubmit'
    });
  const validFrom = useWatch({ control, name: 'validFrom' });

  useEffect(() => {
    if (schedule) {
      reset(getCopyDefaults(schedule, new Date()));
    }
  }, [schedule, reset]);

  const { mutate: copy, isLoading } = useCopyOpeningSchedule(
    planUnitId,
    onCopied
  );

  const onSubmit = (values: CopyOpeningScheduleFormValues) => {
    if (!schedule) return;
    copy({
      openingScheduleId: schedule.openingScheduleId,
      copy: {
        name: values.name.trim(),
        validFrom: toOptionalDateString(toNativeDate(values.validFrom)),
        validTo: toOptionalDateString(toNativeDate(values.validTo)) || undefined
      }
    });
  };

  const closedOn =
    schedule && validFrom && closesOriginal(schedule, validFrom)
      ? validFrom
      : undefined;

  return (
    <Dialog
      open={!!schedule}
      title={t('copyTitle')}
      handleClose={onClose}
      maxWidth="medium"
      fullWidth
      actions={
        <StyledCopyActions>
          <Button variant="outlined" onClick={onClose}>
            {t('cancel')}
          </Button>
          <LoadingButton
            variant="contained"
            loading={isLoading}
            onClick={handleSubmit(onSubmit)}
          >
            {t('copy')}
          </LoadingButton>
        </StyledCopyActions>
      }
    >
      <StyledCopyFields>
        <ControlledTextField
          control={control}
          name="name"
          label={t('name')}
          required
          fullWidth
        />
        <ControlledValidFromValidTo
          control={control}
          validFromFieldName="validFrom"
          validToFieldName="validTo"
          validFromRequired
        />
        {schedule && closedOn && (
          <Alert severity="info">
            {t('copyClosesOriginal', {
              name: schedule.name,
              date: formatDate(closedOn.toISOString())
            })}
          </Alert>
        )}
      </StyledCopyFields>
    </Dialog>
  );
};

export default CopyOpeningScheduleDialog;
